// ═══════════════════════════════════════════════════
//  Wrapped surface area for upholstery labor scoring.
//  Models a product as a box and sums the faces that
//  actually receive fabric.
//  See spec: docs/superpowers/specs/2026-08-03-wrapped-surface-fabric-faces-design.md
// ═══════════════════════════════════════════════════

/** Which of the six box faces receive fabric. */
export interface FaceSelection {
  front: boolean;
  back: boolean;
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
}

/** Box dimensions. ALL VALUES IN INCHES — callers convert, this module never does. */
export interface BoxDims {
  width_in: number;
  height_in: number;
  depth_in: number;
}

export const ALL_FACES: FaceSelection = {
  front: true,
  back: true,
  top: true,
  bottom: true,
  left: true,
  right: true,
};

/**
 * Normalise a stored selection into a complete one.
 * A missing object, a null, or an absent key all mean "covered" — the single
 * place that rule lives, so items saved before face selection existed keep
 * scoring as fully wrapped.
 */
export function resolveFaces(faces?: Partial<FaceSelection> | null): FaceSelection {
  if (!faces) return { ...ALL_FACES };
  return {
    front: faces.front ?? true,
    back: faces.back ?? true,
    top: faces.top ?? true,
    bottom: faces.bottom ?? true,
    left: faces.left ?? true,
    right: faces.right ?? true,
  };
}

/**
 * Area of each face in square inches.
 * Faces come in three matched pairs, so every dimension appears in two of
 * them — no dimension can drop out of the total.
 */
export function faceAreas(dims: BoxDims): Record<keyof FaceSelection, number> {
  // Clamp rather than throw: a half-configured item should score 0, not break
  // the whole estimate.
  const w = Math.max(0, dims.width_in || 0);
  const h = Math.max(0, dims.height_in || 0);
  const d = Math.max(0, dims.depth_in || 0);
  return {
    front: w * h,
    back: w * h,
    top: w * d,
    bottom: w * d,
    left: d * h,
    right: d * h,
  };
}

/** Total covered surface in square feet. */
export function wrappedSurfaceSqft(
  dims: BoxDims,
  faces?: Partial<FaceSelection> | null,
): number {
  const selected = resolveFaces(faces);
  const areas = faceAreas(dims);
  let totalSqIn = 0;
  for (const key of Object.keys(areas) as (keyof FaceSelection)[]) {
    if (selected[key]) totalSqIn += areas[key];
  }
  return totalSqIn / 144;
}
